import React, { useContext, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { CampaignContext } from "../../contexts/CampaignContext";
import { AuthContext } from "../../contexts/AuthContext";
import { IconJsxer } from "../../helpers/IconHelper";
import { ACCOUNT_TYPES } from "../../helpers/AccountTypes";
import { TOP_LEVEL_NAV_ROUTES } from "../../config/NavRoutes";
import config from "../../config";
import StreamDetailsModal from "../StreamDetailsModal/StreamDetailsModal";
import "./StreamsDropdown.scss";

const StreamsDropdown = ({ isOpen, onClose, campaignId, onAddStream, onEditStream, editingStreamId }) => {
    const [campaignContext] = useContext(CampaignContext);
    const [authContext] = useContext(AuthContext);
    const navigate = useNavigate();
    const [tooltipPosition, setTooltipPosition] = useState(null);
    const [hoveredStreamId, setHoveredStreamId] = useState(null);
    const [selectedStream, setSelectedStream] = useState(null);

    // Sort streams by start time (soonest first) and filter out archived streams
    const streams = (campaignContext.campaignData?.streams || [])
        .filter(stream => !stream.archived)
        .slice()
        .sort((a, b) => {
            return a.startTime - b.startTime;
        });

    if (!isOpen) return null;

    const handleStreamClick = (stream, e) => {
        // Don't open modal if clicking on buttons or interactive elements
        if (e.target.closest('button') || e.target.closest('.channel-icons')) {
            return;
        }
        setSelectedStream(stream);
    };

    const handleEditStream = (stream, e) => {
        e.stopPropagation();
        if (onEditStream) {
            onEditStream(stream);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('OBS link copied to clipboard!');
    };

    const getOBSLink = (stream) => {
        return `${config.axiosOrigin}/obs-feature/${campaignId}/${stream._id}/${stream.livePIN}`;
    };

    const openStream = (streamId) => {
        navigate(TOP_LEVEL_NAV_ROUTES.MANAGE_CAMPAIGN_STREAM.replace(':campaignId', campaignId).replace(':streamId', streamId));
    };

    const formatDateTime = (timestamp, timezone) => {
        const date = new Date(timestamp);
        const options = { 
            weekday: 'short', 
            day: '2-digit', 
            month: 'short', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        };
        return date.toLocaleString('en-US', options);
    };

    // Get channel icons (max 2 visible + count)
    const getChannelIcons = (stream) => {
        const channels = stream.channels || [];
        const visibleChannels = channels.slice(0, 2);
        const additionalCount = channels.length - 2;

        const openTwitchChannel = (channelName) => {
            window.open(`https://www.twitch.tv/${channelName}`, '_blank');
        };

        const handleMouseEnter = (e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setTooltipPosition({
                top: rect.bottom - 8,
                right: window.innerWidth - rect.right
            });
            setHoveredStreamId(stream._id);
        };

        const handleMouseLeave = (e) => {
            // Only hide if not moving to the tooltip
            const relatedTarget = e.relatedTarget;
            if (relatedTarget && relatedTarget.closest('.channel-tooltip-fixed')) {
                return;
            }
            setHoveredStreamId(null);
            setTooltipPosition(null);
        };

        const handleTooltipMouseLeave = () => {
            setHoveredStreamId(null);
            setTooltipPosition(null);
        };

        return (
            <div 
                className="channel-icons-wrapper"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <div className="channel-icons">
                    {visibleChannels.map((channel, idx) => (
                        <div key={idx} className="channel-icon">
                            {channel.profilePic ? (
                                <img src={channel.profilePic} alt={channel.twitchHandle} className="channel-avatar" />
                            ) : (
                                <div className="channel-avatar"></div>
                            )}
                        </div>
                    ))}
                    {additionalCount > 0 && (
                        <div className="channel-icon additional-count">
                            +{additionalCount}
                        </div>
                    )}
                </div>
                {channels.length > 0 && hoveredStreamId === stream._id && tooltipPosition && (
                    <div 
                        className="channel-tooltip-fixed"
                        style={{
                            position: 'fixed',
                            top: `${tooltipPosition.top}px`,
                            right: `${tooltipPosition.right}px`
                        }}
                        onMouseLeave={handleTooltipMouseLeave}
                    >
                        {channels.map((channel, idx) => (
                            <div 
                                key={idx} 
                                className="tooltip-channel-item"
                                onClick={() => openTwitchChannel(channel.twitchHandle)}
                            >
                                {channel.profilePic ? (
                                    <img src={channel.profilePic} alt={channel.twitchHandle} className="tooltip-avatar" />
                                ) : (
                                    <div className="tooltip-avatar-placeholder"></div>
                                )}
                                <span>{channel.twitchHandle}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            {selectedStream && (
                <StreamDetailsModal 
                    stream={selectedStream}
                    campaignId={campaignId}
                    onClose={() => setSelectedStream(null)}
                />
            )}
            {createPortal(
                <div className="streams-dropdown-overlay" onClick={onClose}></div>,
                document.body
            )}
            <div className="streams-dropdown">
                <div className="streams-dropdown-header">
                    <h3>Campaign streams</h3>
                    <button className="close-button" onClick={onClose}>
                        {IconJsxer.GetIcon(IconJsxer.ICONS.closeX)}
                        <span>Close</span>
                    </button>
                </div>

                <div className="streams-list">
                    {streams.map((stream) => (
                        <div 
                            key={stream._id} 
                            className={`stream-item ${editingStreamId === stream._id ? 'editing' : ''} ${stream.demoMode ? 'stream-test' : 'stream-live'}`}
                            onClick={(e) => handleStreamClick(stream, e)}
                        >
                            <div className="stream-left">
                                <div className="stream-name-row">
                                    <h4>{stream.name || stream.twitchHandle || 'Unnamed Stream'}</h4>
                                    {stream.status === 'live' && (
                                        <div className="status-indicator live">
                                            <span className="status-dot"></span>
                                            <span className="status-text">ON AIR</span>
                                        </div>
                                    )}
                                    {stream.status === 'ended' && (
                                        <div className="status-indicator ended">
                                            <span className="status-dot"></span>
                                            <span className="status-text">STREAM ENDED</span>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="stream-date-row">
                                    <span className="stream-type">
                                        {stream.demoMode ? 'Live Test' : 'Live Stream'}
                                    </span>
                                    <span className="stream-datetime">
                                        {formatDateTime(stream.startTime, stream.timeZone)}
                                    </span>
                                </div>
                                
                                <div className="obs-link-row">
                                    <label>OBS link</label>
                                    <button 
                                        className="copy-link-btn"
                                        onClick={() => copyToClipboard(getOBSLink(stream))}
                                    >
                                        {IconJsxer.GetIcon(IconJsxer.ICONS.link)}
                                        <span>Copy link</span>
                                    </button>
                                </div>
                            </div>
                            
                            <div className="stream-right">
                                <div className="stream-buttons">
                                    <button 
                                        className="edit-icon-btn"
                                        onClick={(e) => handleEditStream(stream, e)}
                                    >
                                        {IconJsxer.GetIcon(IconJsxer.ICONS.edit)}
                                    </button>
                                    <button 
                                        className="open-stream-btn"
                                        onClick={() => openStream(stream._id)}
                                    >
                                        Open stream
                                    </button>
                                </div>
                                {getChannelIcons(stream)}
                            </div>
                        </div>
                    ))}
                </div>

                {authContext.userData.userLevel >= ACCOUNT_TYPES.producer.level && (
                    <div className="streams-dropdown-footer">
                        <button className="create-stream-btn" onClick={onAddStream}>
                            Create new stream
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

export default StreamsDropdown;
